import { FeatureOption, FilterKeys, Filters, Option } from '@utils/pageConfig/Types';
import { BaseTimePeriod } from '@services/FiltersTypes';
import { Periodicity } from '@components/time-period-selector/PeriodsTypes';
import { byFilterKey, excludeByFilterKey } from '@utils/pageConfig/Predicates/SelectedFiltersPredicates';
import { PagesConfig } from '@utils/pageConfig/PagesConfigContext';
import { ProductType } from '@pages/product-analyzer/Product';
import { isLocalCurrencyApplied } from '@utils/currencyUtils/CurrencyUtils';
import { ExploreChartFiltersType } from '@utils/exploreUtils/ExploreType';
import { Country } from '@components/context-bar/Types';
import { multiCountryDisabledFilters } from '@utils/multiCountryUtils/MultiCountryUtils';
import { filterOutAddSegFeat, removeDuplicateSegByFilterOption } from '@utils/design/ButtonMenuUtils';
import PGLimit from '@const/PGLimit';

export const filterCurrencyFilterForWeeklyYTDOrMAT = (
  currentAppliedFilters: Filters[],
  period: BaseTimePeriod,
  periodType: Periodicity,
): Filters[] => {
  return currentAppliedFilters;
};

export const removeFeaturesFromDropdownBasedOnSideFilters = (
  currentFeatureValues: Filters[],
  pagesConfig: PagesConfig,
  countries: Country[],
  appliedProductGroups: Option[] | undefined,
): Filters[] => {
  let filteredFeatureValues: Filters[] = currentFeatureValues;

  //Applied Single Product Groups, Category selection will be disabled
  if (appliedProductGroups?.length === 1) {
    filteredFeatureValues = filteredFeatureValues.filter(excludeByFilterKey(FilterKeys.CATEGORY));
  }

  //Applied Single Product Groups and Single Country, or multi Product groups, Country selection will be disabled
  if ((countries.length === 1 && appliedProductGroups?.length === 1) || appliedProductGroups?.length !== 1) {
    filteredFeatureValues = filteredFeatureValues.filter(excludeByFilterKey(FilterKeys.COUNTRIES_FEATURE));
  }

  return filteredFeatureValues;
};

export const removeFeaturesFromDropdownBasedOnSelectedFeatures = (
  exploreSegmentChartFiltersType: ExploreChartFiltersType,
  currentFeatureValues: Filters[],
  selectedFilters: FeatureOption,
  appliedProductGroup: Option[] | undefined,
  isChannelFilterAllowed: boolean,
): Filters[] => {
  let filteredFeatureValues: Filters[] = currentFeatureValues;

  if (exploreSegmentChartFiltersType === ExploreChartFiltersType.SegmentByFeature) {
    filteredFeatureValues = removeDuplicateSegByFilterOption(filteredFeatureValues, selectedFilters);
  }

  if (
    [ExploreChartFiltersType.ShowPerformanceWithinFeature, ExploreChartFiltersType.SegmentByFeature].includes(
      exploreSegmentChartFiltersType,
    )
  ) {
    filteredFeatureValues = removeChannelFeatureFromDropdown(
      filteredFeatureValues,
      appliedProductGroup,
      isChannelFilterAllowed,
    );
  }

  if (appliedProductGroup && appliedProductGroup.length === 0) {
    filteredFeatureValues = filterOutAddSegFeat(filteredFeatureValues, selectedFilters);
  }

  return filteredFeatureValues;
};

/**
 *
 * @param currentFeatureValues - current feature values from feature value API
 * @param appliedProductGroup - applied product groups.
 * @param isChannelFilterAllowed - if channel is enabled for the country.
 * This function is used to remove Channel from the showPerformanceWithin dropdown in the stacked chart.
 * Currently if country France is selected, we will hide Channel from showPerformanceWithin.
 * So if view is stacked chart and country is France, then we will return features without Channel.
 *
 */
export const removeChannelFeatureFromDropdown = (
  currentFeatureValues: Filters[],
  appliedProductGroup: Option[] | undefined,
  isChannelFilterAllowed: boolean,
): Filters[] => {
  if (!isChannelFilterAllowed && appliedProductGroup?.length !== 1) {
    return currentFeatureValues.filter(excludeByFilterKey(FilterKeys.CHANNEL));
  }
  return currentFeatureValues;
};

export const getDisabledFilterKeysByAppliedCurrency = (
  allFilters: Filters[],
  pagesConfig: PagesConfig,
  effectiveProductType: ProductType,
): FilterKeys[] => {
  const appliedCountry = allFilters.find(byFilterKey(FilterKeys.COUNTRY));
  const appliedCurrency = allFilters.find(byFilterKey(FilterKeys.CURRENCY));

  if (
    !appliedCountry ||
    !appliedCurrency ||
    isLocalCurrencyApplied(pagesConfig, effectiveProductType, appliedCurrency, appliedCountry)
  ) {
    return [];
  }
  return multiCountryDisabledFilters;
};

export const filterAppliedFiltersForStackedChartRequest = (
  allFilters: Filters[],
  period: BaseTimePeriod,
  periodType: Periodicity,
) => {
  let filteredAppliedFilters: Filters[];

  filteredAppliedFilters = filterCurrencyFilterForWeeklyYTDOrMAT(allFilters, period, periodType);
  filteredAppliedFilters = filteredAppliedFilters.filter(excludeByFilterKey(FilterKeys.PRODUCT_GROUP));
  filteredAppliedFilters = filteredAppliedFilters.filter(excludeByFilterKey(FilterKeys.COUNTRY));

  return filteredAppliedFilters;
};

export const shouldLimitProductGroup = (
  noOfAppliedPG: number,
  totalPGs: number,
  feature: FeatureOption,
  segmentBy?: FeatureOption,
) => {
  const isTotalPGMoreThanLimit = totalPGs > PGLimit.PG_LIMIT;
  // When noOfAppliedPG is 0, it means all PGs are applied (not none).
  const isAppliedPGMoreThanLimit = noOfAppliedPG === 0 || noOfAppliedPG > PGLimit.PG_LIMIT;
  const isBrandFilterApplied = [feature.filterKey, segmentBy?.filterKey].includes(FilterKeys.BRAND);

  return isTotalPGMoreThanLimit && isAppliedPGMoreThanLimit && isBrandFilterApplied;
};
